/**
 * Typed client for the FactSet Text-to-Formula API surfaces exposed by
 * the add-in server under `/api/factset/ttf/*`.
 *
 * Phase 3 — only used by the dev validation harness
 * (`components/dev/TtfTestPanel.tsx`). The real Excel AI flows
 * (chat / model creation / charts) integrate against this module in
 * Phase 4 once we've confirmed the end-to-end plumbing works.
 *
 * Errors are surfaced as typed `Error` subclasses so callers can branch
 * on `instanceof` without sniffing message strings:
 *   - `TtfReauthRequiredError` — user must reconnect FactSet OAuth
 *   - `TtfTimeoutError`         — poll loop exceeded its budget
 *   - `TtfCancelledError`       — caller signalled `AbortSignal.abort()`
 *   - `TtfFailedError`          — generic transport / upstream failure
 *
 * Error codes, route paths, status literals, and OAuth scope tokens come
 * from `factsetConstants.ts`, which is the typed mirror of the server-side
 * `apps/shared/factsetConstants.js`. A parity test in
 * `apps/server/__tests__/factsetConstants.parity.test.mjs` enforces that
 * the two never drift.
 */

import { apiFetch } from "../../../shared/apiClient";
import { diag, recordPhase } from "../../aiDiag";
import {
  FACTSET_ROUTES,
  FACTSET_TTF_STATUS,
  REAUTH_REQUIRED_OAUTH_ERROR_CODES,
  type FactSetTtfStatus,
} from "../../../shared/factset/factsetConstants";

/** Successful response from `POST /api/factset/ttf/generate`. */
export interface TtfSubmitResult {
  jobId: string;
  /** UUIDv4 echoed back from the server-side `X-Fds-Chain-Id` header for log correlation. */
  chainId: string;
}

/** Wrapped poll response from `GET /api/factset/ttf/result`. */
export interface TtfResultEnvelope {
  status: FactSetTtfStatus;
  chainId: string;
  /** Raw upstream JSON when `status === "ok"`. Shape is owned by FactSet. */
  result?: unknown;
}

/**
 * Thrown when the FactSet OAuth grant is missing or no longer valid.
 * The `reauthorizationUrl` field is the server-provided endpoint that the
 * client should send the user to via the existing OAuth start flow.
 */
export class TtfReauthRequiredError extends Error {
  reauthorizationUrl: string;
  serverErrorCode: string;
  constructor(message: string, reauthorizationUrl: string, serverErrorCode: string) {
    super(message);
    this.name = "TtfReauthRequiredError";
    this.reauthorizationUrl = reauthorizationUrl;
    this.serverErrorCode = serverErrorCode;
    // Restore the prototype chain under ES5 target — `super(message)`
    // would otherwise leave `instanceof TtfReauthRequiredError`
    // returning false, causing `ttfCellDispatch` to route reauth-
    // required failures into the generic failure branch instead of
    // triggering the OAuth re-grant flow.
    Object.setPrototypeOf(this, TtfReauthRequiredError.prototype);
  }
}

/** Thrown when the poll loop's total time budget is exhausted. */
export class TtfTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TtfTimeoutError";
    Object.setPrototypeOf(this, TtfTimeoutError.prototype);
  }
}

/**
 * Thrown when the caller explicitly aborts an in-flight TTF call via the
 * `AbortSignal` they passed in. Separate from `TtfTimeoutError` so callers
 * can render cancellation as a benign neutral state rather than a failure.
 */
export class TtfCancelledError extends Error {
  constructor(message = "TTF call cancelled by caller.") {
    super(message);
    this.name = "TtfCancelledError";
    Object.setPrototypeOf(this, TtfCancelledError.prototype);
  }
}

/** Thrown for any other transport / upstream failure (4xx, 5xx, network). */
export class TtfFailedError extends Error {
  serverErrorCode?: string;
  serverDetails?: string;
  httpStatus?: number;
  constructor(
    message: string,
    options: { serverErrorCode?: string; serverDetails?: string; httpStatus?: number } = {}
  ) {
    super(message);
    this.name = "TtfFailedError";
    this.serverErrorCode = options.serverErrorCode;
    this.serverDetails = options.serverDetails;
    this.httpStatus = options.httpStatus;
    Object.setPrototypeOf(this, TtfFailedError.prototype);
  }
}

/**
 * Raised before an outbound request when a numeric absolute date cannot be
 * safely represented as the MM/DD/YYYY value required by TTF.
 */
export class TtfQueryDatePolicyError extends Error {
  readonly code = "factset_ttf_invalid_query_date";
  readonly offendingText: string;

  constructor(message: string, offendingText: string) {
    super(message);
    this.name = "TtfQueryDatePolicyError";
    this.offendingText = offendingText;
    Object.setPrototypeOf(this, TtfQueryDatePolicyError.prototype);
  }
}

function isValidCalendarDate(year: number, month: number, day: number): boolean {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return false;
  }
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

function formatUsAbsoluteDate(year: number, month: number, day: number): string {
  return `${String(month).padStart(2, "0")}/${String(day).padStart(2, "0")}/${String(year).padStart(4, "0")}`;
}

function invalidQueryDate(offendingText: string, reason: string): never {
  throw new TtfQueryDatePolicyError(
    `${reason} "${offendingText}" in TTF query. Use MM/DD/YYYY for numeric absolute dates.`,
    offendingText,
  );
}

/**
 * Canonicalize numeric absolute dates at the final TTF transport boundary.
 *
 * Natural-language dates and relative/fiscal periods are intentionally left
 * alone. Slash dates are interpreted as US dates; an otherwise unambiguous
 * day-first value is safely reordered. Ambiguous hyphenated values are
 * rejected instead of guessed.
 */
export function normalizeTtfQueryAbsoluteDates(query: string): string {
  const yearFirstPattern = /(^|[^0-9])(\d{4})([-/])(\d{1,2})\3(\d{1,2})(?!\d)/g;
  let normalized = query.replace(
    yearFirstPattern,
    (
      match: string,
      prefix: string,
      yearText: string,
      _separator: string,
      monthText: string,
      dayText: string,
    ) => {
      const year = Number(yearText);
      const month = Number(monthText);
      const day = Number(dayText);
      const offendingText = match.slice(prefix.length);
      if (!isValidCalendarDate(year, month, day)) {
        return invalidQueryDate(offendingText, "Invalid absolute date");
      }
      return `${prefix}${formatUsAbsoluteDate(year, month, day)}`;
    },
  );

  const shortFirstPattern = /(^|[^0-9])(\d{1,2})([-/])(\d{1,2})\3(\d{4})(?!\d)/g;
  normalized = normalized.replace(
    shortFirstPattern,
    (
      _match,
      prefix: string,
      firstText: string,
      separator: string,
      secondText: string,
      yearText: string,
    ) => {
      const first = Number(firstText);
      const second = Number(secondText);
      const year = Number(yearText);
      const offendingText = `${firstText}${separator}${secondText}${separator}${yearText}`;

      let month: number;
      let day: number;
      if (separator === "/") {
        if (first >= 1 && first <= 12) {
          month = first;
          day = second;
        } else if (first >= 13 && first <= 31 && second >= 1 && second <= 12) {
          month = second;
          day = first;
        } else {
          return invalidQueryDate(offendingText, "Invalid absolute date");
        }
      } else if (first >= 1 && first <= 12 && second >= 1 && second <= 12) {
        return invalidQueryDate(offendingText, "Ambiguous non-US absolute date");
      } else if (first >= 1 && first <= 12 && second >= 13 && second <= 31) {
        month = first;
        day = second;
      } else if (first >= 13 && first <= 31 && second >= 1 && second <= 12) {
        month = second;
        day = first;
      } else {
        return invalidQueryDate(offendingText, "Invalid absolute date");
      }

      if (!isValidCalendarDate(year, month, day)) {
        return invalidQueryDate(offendingText, "Invalid absolute date");
      }
      return `${prefix}${formatUsAbsoluteDate(year, month, day)}`;
    },
  );

  return normalized;
}

interface ParsedApiError {
  error?: string;
  message?: string;
  details?: string;
  reauthorizationUrl?: string;
}

async function readErrorBody(response: Response): Promise<ParsedApiError> {
  try {
    const body = await response.clone().json();
    if (body && typeof body === "object") {
      return body as ParsedApiError;
    }
  } catch {
    // fall through to text
  }
  try {
    const text = await response.text();
    return { message: text || `HTTP ${response.status}` };
  } catch {
    return { message: `HTTP ${response.status}` };
  }
}

/**
 * Inspects an `apiFetch` non-OK response and throws the most specific
 * typed error. Centralized so the generate + result code paths cannot
 * drift on which server error codes count as "needs reconnect".
 */
async function throwForResponse(response: Response): Promise<never> {
  const body = await readErrorBody(response);
  const code = typeof body.error === "string" ? body.error : "";
  const message = body.message || `FactSet TTF request failed (${response.status})`;

  if (REAUTH_REQUIRED_OAUTH_ERROR_CODES.has(code as never)) {
    throw new TtfReauthRequiredError(
      message,
      typeof body.reauthorizationUrl === "string" && body.reauthorizationUrl
        ? body.reauthorizationUrl
        : FACTSET_ROUTES.OAUTH_START,
      code
    );
  }

  throw new TtfFailedError(message, {
    serverErrorCode: code || undefined,
    serverDetails: body.details,
    httpStatus: response.status,
  });
}

/** True when an Error originated from `AbortSignal.abort()`. */
function isAbortError(error: unknown): boolean {
  return (
    error instanceof DOMException && error.name === "AbortError"
  ) || (error instanceof Error && error.name === "AbortError");
}


/**
 * Submits a natural-language query to the server's TTF proxy. The server
 * forwards it to FactSet with the user's OAuth token and returns the
 * upstream `job_id` plus the per-call chain id.
 *
 * Honors `options.signal` for cancellation — when the signal aborts mid-flight
 * the underlying fetch is cancelled and a `TtfCancelledError` is thrown.
 */
export async function submitTtfQuery(
  query: string,
  options: { signal?: AbortSignal } = {}
): Promise<TtfSubmitResult> {
  const normalizedQuery = normalizeTtfQueryAbsoluteDates(query);
  diag("api", "ttf_submit", {
    query: normalizedQuery,
    ...(normalizedQuery !== query ? { originalQuery: query } : {}),
  });
  const submitStartedAt = Date.now();
  let response: Response;
  try {
    response = await apiFetch(FACTSET_ROUTES.TTF_GENERATE, {
      method: "POST",
      body: JSON.stringify({ query: normalizedQuery }),
      signal: options.signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw new TtfCancelledError("TTF submit cancelled by caller.");
    }
    throw error;
  }
  if (!response.ok) {
    await throwForResponse(response);
  }
  const payload = (await response.json()) as TtfSubmitResult;
  recordPhase("ttf:submit", Date.now() - submitStartedAt);
  return payload;
}

// Historical fixed poll cadence: `pollTtfResult` issues an immediate first
// poll, then waits 4s before the first retry and 2s between later retries.
// This is intentionally less front-loaded than the previous sub-second cadence
// to reduce TTF poll pressure while preserving the same total budget.
const POLL_DELAYS_MS = [
  4000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000,
];
const DEFAULT_TOTAL_POLL_BUDGET_MS = 25_000;

/**
 * Polls `GET /api/factset/ttf/result` until the job completes or the
 * total time budget is exhausted. Inter-poll delays follow the fixed
 * `POLL_DELAYS_MS` cadence.
 * Honors `options.signal` so the dev panel's "Cancel" button can interrupt
 * mid-poll — both the inter-poll sleep AND the in-flight fetch will abort.
 *
 * Rejects with `TtfFailedError` if the server returns an unexpected
 * envelope `status` (anything other than `ok` / `pending`); polling
 * forever on an unknown literal would otherwise hide a server bug.
 */
export async function pollTtfResult(
  jobId: string,
  options: { signal?: AbortSignal; totalBudgetMs?: number } = {}
): Promise<unknown> {
  const totalBudget = options.totalBudgetMs ?? DEFAULT_TOTAL_POLL_BUDGET_MS;
  const startedAt = Date.now();
  let attempt = 0;

  while (true) {
    if (options.signal?.aborted) {
      throw new TtfCancelledError("TTF poll cancelled by caller.");
    }
    if (Date.now() - startedAt >= totalBudget) {
      throw new TtfTimeoutError(
        `TTF poll exceeded ${Math.round(totalBudget / 1000)}s time budget.`
      );
    }

    let response: Response;
    try {
      response = await apiFetch(
        `${FACTSET_ROUTES.TTF_RESULT}?jobId=${encodeURIComponent(jobId)}`,
        { method: "GET", signal: options.signal }
      );
    } catch (error) {
      if (isAbortError(error)) {
        throw new TtfCancelledError("TTF poll cancelled by caller.");
      }
      throw error;
    }
    if (!response.ok) {
      await throwForResponse(response);
    }
    const envelope = (await response.json()) as TtfResultEnvelope;
    if (envelope.status === FACTSET_TTF_STATUS.OK) {
      recordPhase("ttf:poll", Date.now() - startedAt, { polls: attempt + 1 });
      return envelope.result;
    }
    if (envelope.status !== FACTSET_TTF_STATUS.PENDING) {
      // Defensive: never silently loop on an unknown status. The server
      // is supposed to only emit "ok" / "pending", so anything else is a
      // contract bug worth surfacing immediately.
      throw new TtfFailedError(
        `Unexpected TTF result status: ${String(envelope.status)}`,
        { serverErrorCode: "factset_ttf_invalid_response" }
      );
    }

    const delayMs = POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)];
    await new Promise<void>((resolve, reject) => {
      if (options.signal?.aborted) {
        reject(new TtfCancelledError("TTF poll cancelled by caller."));
        return;
      }
      const timer = setTimeout(() => {
        if (options.signal) {
          options.signal.removeEventListener("abort", onAbort);
        }
        resolve();
      }, delayMs);
      const onAbort = () => {
        clearTimeout(timer);
        reject(new TtfCancelledError("TTF poll cancelled by caller."));
      };
      options.signal?.addEventListener("abort", onAbort, { once: true });
    });
    attempt += 1;
  }
}

/** HTTP status codes considered transient — safe to retry once. */
const TRANSIENT_HTTP_CODES = new Set([429, 502, 503]);

/** Delay before a single retry attempt on a transient failure. */
const RETRY_DELAY_MS = 2000;

/**
 * Sleep for `ms` while preserving cooperative cancellation.
 *
 * @param ms - Number of milliseconds to wait before resolving.
 * @param signal - Optional abort signal. When aborted before or during the
 *   wait, rejects with `TtfCancelledError`.
 * @returns A promise that resolves after `ms` unless cancelled.
 * @throws TtfCancelledError if `signal` is already aborted or aborts while waiting.
 */
function sleepBeforeRetry(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new TtfCancelledError("TTF retry cancelled by caller."));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new TtfCancelledError("TTF retry cancelled by caller."));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Build per-attempt options using the caller's remaining total budget.
 *
 * @param options - Original `runTtfQuery` options.
 * @param startedAt - Timestamp captured before the first submit attempt.
 * @returns Options with `totalBudgetMs` reduced by elapsed wall-clock time,
 *   or the original options when no total budget was supplied.
 */
function optionsWithRemainingBudget(
  options: { signal?: AbortSignal; totalBudgetMs?: number },
  startedAt: number
): { signal?: AbortSignal; totalBudgetMs?: number } {
  if (options.totalBudgetMs === undefined) return options;
  return {
    ...options,
    totalBudgetMs: Math.max(0, options.totalBudgetMs - (Date.now() - startedAt)),
  };
}

/**
 * Convenience wrapper that submits a query and waits for its result in
 * one call. Returns the upstream JSON payload (FactSet-owned shape).
 *
 * Includes a single automatic retry for transient HTTP failures (429, 502,
 * 503) with a 2-second backoff. This absorbs momentary FactSet upstream
 * capacity issues without burning an LLM tool-loop iteration on a rephrase.
 *
 * Forwards `options.signal` to both the submit and the poll calls so the
 * caller has a single switch that aborts the entire workflow regardless
 * of which phase is in flight. Returns the chain id of the *generate*
 * request (the poll-side chain ids are surfaced via server logs only —
 * we deliberately do not aggregate them here so the success path keeps
 * one stable identifier).
 *
 * Errors propagate as typed exceptions — callers should branch on
 * `instanceof TtfReauthRequiredError` before falling back to generic
 * failure handling.
 *
 * @param query - The natural-language question to submit to the TTF API.
 * @param options - Optional configuration.
 * @param options.signal - `AbortSignal` for cooperative cancellation. When
 *   aborted, no retry is attempted and a `TtfCancelledError` propagates.
 * @param options.totalBudgetMs - Maximum wall-clock milliseconds shared by
 *   the initial submit+poll attempt, retry delay, and retry poll attempt.
 *   Defaults to `DEFAULT_TOTAL_POLL_BUDGET_MS` inside `pollTtfResult`.
 * @returns An object containing the `jobId`, `chainId`, and the raw upstream
 *   `result` payload.
 * @throws TtfReauthRequiredError if FactSet returns a 401/403.
 * @throws TtfTimeoutError if the poll exceeds the time budget.
 * @throws TtfCancelledError if `signal` is aborted.
 * @throws TtfFailedError for non-transient upstream failures or if the
 *   single retry also fails.
 */
export async function runTtfQuery(
  query: string,
  options: { signal?: AbortSignal; totalBudgetMs?: number } = {}
): Promise<{ jobId: string; chainId: string; result: unknown }> {
  const startedAt = Date.now();
  try {
    const submission = await submitTtfQuery(query, { signal: options.signal });
    const result = await pollTtfResult(submission.jobId, optionsWithRemainingBudget(options, startedAt));
    recordPhase("ttf:total", Date.now() - startedAt, { retried: false });
    return { jobId: submission.jobId, chainId: submission.chainId, result };
  } catch (err) {
    if (
      err instanceof TtfFailedError &&
      err.httpStatus !== undefined &&
      TRANSIENT_HTTP_CODES.has(err.httpStatus) &&
      !options.signal?.aborted
    ) {
      await sleepBeforeRetry(RETRY_DELAY_MS, options.signal);
      const retryOptions = optionsWithRemainingBudget(options, startedAt);
      if (retryOptions.totalBudgetMs !== undefined && retryOptions.totalBudgetMs <= 0) {
        throw new TtfTimeoutError(
          `TTF retry exceeded ${Math.round(options.totalBudgetMs! / 1000)}s time budget.`
        );
      }
      const submission = await submitTtfQuery(query, { signal: options.signal });
      const result = await pollTtfResult(submission.jobId, retryOptions);
      recordPhase("ttf:total", Date.now() - startedAt, { retried: true });
      return { jobId: submission.jobId, chainId: submission.chainId, result };
    }
    throw err;
  }
}
